import { useEffect, useMemo, useRef, useState } from "react";
import "./FormContact.css";

type FormState = {
  name: string;
  company: string;
  email: string;
  phone: string;
  city: string;
  projectType: string;
  services: string[];
  timeline: string;
  budget: string;
  message: string;
  consent: boolean;
};

const SERVICE_OPTIONS = [
  "Diseño Arquitectónico",
  "Obra Civil",
  "Instalaciones Eléctricas",
  "Instalaciones Especiales",
  "Telecomunicaciones",
  "Implementaciones Tecnológicas",
] as const;

const PROJECT_TYPES = [
  "Residencial",
  "Comercial",
  "Industrial",
  "Gobierno",
  "Otro",
] as const;

const TIMELINES = [
  "Urgente (1–2 semanas)",
  "Corto (1–2 meses)",
  "Medio (3–6 meses)",
  "Largo (6+ meses)",
  "A definir",
] as const;

const BUDGETS = [
  "A definir",
  "Menos de $50k",
  "$50k – $150k",
  "$150k – $500k",
  "$500k+",
] as const;

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReduced(!!mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

function NetworkCanvas() {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    if (reducedMotion) return;

    const canvas = ref.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let raf = 0;

    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const parent = canvas.parentElement;
      const w = parent?.clientWidth ?? window.innerWidth;
      const h = parent?.clientHeight ?? 520;

      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    window.addEventListener("resize", resize);

    const nodes = Array.from({ length: 38 }).map(() => ({
      x: Math.random() * (canvas.clientWidth || 800),
      y: Math.random() * (canvas.clientHeight || 520),
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      r: 1.2 + Math.random() * 1.8,
    }));

    const step = () => {
      const w = canvas.clientWidth || 800;
      const h = canvas.clientHeight || 520;

      ctx.clearRect(0, 0, w, h);

      // soft vignette (depth)
      const grad = ctx.createRadialGradient(w * 0.55, h * 0.35, 50, w * 0.55, h * 0.35, Math.max(w, h));
      grad.addColorStop(0, "rgba(120, 140, 255, 0.10)");
      grad.addColorStop(1, "rgba(0, 0, 0, 0)");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      // move nodes
      for (const p of nodes) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -20) p.x = w + 20;
        if (p.x > w + 20) p.x = -20;
        if (p.y < -20) p.y = h + 20;
        if (p.y > h + 20) p.y = -20;
      }

      // links
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const a = nodes[i];
          const b = nodes[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const d = Math.hypot(dx, dy);
          const max = 140;

          if (d < max) {
            const alpha = (1 - d / max) * 0.22;
            ctx.strokeStyle = `rgba(140, 170, 255, ${alpha})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // nodes
      for (const p of nodes) {
        ctx.fillStyle = "rgba(210, 220, 255, 0.85)";
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, [reducedMotion]);

  return <canvas ref={ref} className="fc-net" aria-hidden="true" />;
}

const FormContact = () => {
  const serviceChips = useMemo(() => SERVICE_OPTIONS, []);

  const [showDetails, setShowDetails] = useState(false);
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  const [data, setData] = useState<FormState>({
    name: "",
    company: "",
    email: "",
    phone: "",
    city: "",
    projectType: "",
    services: [],
    timeline: "",
    budget: "A definir",
    message: "",
    consent: true,
  });

  const toggleService = (s: string) => {
    setData((prev) => {
      const exists = prev.services.includes(s);
      return {
        ...prev,
        services: exists ? prev.services.filter((x) => x !== s) : [...prev.services, s],
      };
    });
  };

  

  const onSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setStatus("sending");

  // ✅ En cPanel: típico quedaría así
  // const endpoint = "/api/contact.php"; 
  const endpoint = "/backend/api/contact.php";

  try {
    const fd = new FormData();

    // Honeypot (campo invisible en UI, pero lo mandamos vacío)
    fd.append("company_website", "");

    fd.append("name", data.name);
    fd.append("company", data.company);
    fd.append("email", data.email);
    fd.append("phone", data.phone);
    fd.append("city", data.city);
    fd.append("projectType", data.projectType);
    fd.append("services", data.services.join(", "));
    fd.append("timeline", data.timeline);
    fd.append("budget", data.budget);
    fd.append("message", data.message);
    fd.append("consent", data.consent ? "Sí" : "No");

    const res = await fetch(endpoint, { method: "POST", body: fd });

    const json = await res.json().catch(() => null);

    if (!res.ok || !json?.ok) throw new Error("Bad response");

    setStatus("sent");
    setShowDetails(false);
    setData((p) => ({
      ...p,
      message: "",
      services: [],
      timeline: "",
      projectType: "",
      budget: "A definir",
    }));
  } catch {
    setStatus("error");
  }
};

  return (
    <section className="fc">
      <div className="fc-bg">
        <NetworkCanvas />
        <div className="fc-fiber" aria-hidden="true" />
      </div>

      <div className="fc-wrap">
        <div className="fc-head">
          <p className="fc-kicker">Contacto</p>
          <h2 className="fc-title">Solicita tu propuesta</h2>
          <p className="fc-subtitle">
            Cuéntanos qué necesitas y te respondemos con una propuesta clara, tiempos y siguientes pasos.
          </p>

          <div className="fc-badges">
            <span className="fc-badge">Respuesta rápida</span>
            <span className="fc-badge">Alcance + tiempos</span>
            <span className="fc-badge">Solución integral</span>
          </div>
        </div>

        <div className="fc-grid">
          {/* Panel izquierdo */}
          <aside className="fc-panel">
            <h3 className="fc-panel-title">Lo que nos ayuda a cotizar mejor</h3>
            <ul className="fc-panel-list">
              <li>Tipo de servicio y alcance.</li>
              <li>Ubicación y tipo de proyecto.</li>
              <li>Urgencia / timeline.</li>
              <li>Contexto (fotos/planos si aplica, se pide después).</li>
            </ul>

            <div className="fc-panel-note">
              Tip: Si no tienes todo, no pasa nada. Con lo básico te contactamos y afinamos el resto.
            </div>
          </aside>

          {/* Form card */}
          <form className="fc-card" onSubmit={onSubmit}>
            <div className="fc-row">
              <label className="fc-label">
                Nombre*
                <input
                  className="fc-input"
                  value={data.name}
                  onChange={(e) => setData((p) => ({ ...p, name: e.target.value }))}
                  required
                  placeholder="Tu nombre"
                />
              </label>

              <label className="fc-label">
                Empresa / Dependencia
                <input
                  className="fc-input"
                  value={data.company}
                  onChange={(e) => setData((p) => ({ ...p, company: e.target.value }))}
                  placeholder="Opcional"
                />
              </label>
            </div>

            <div className="fc-row">
              <label className="fc-label">
                Email*
                <input
                  className="fc-input"
                  type="email"
                  value={data.email}
                  onChange={(e) => setData((p) => ({ ...p, email: e.target.value }))}
                  required
                  placeholder="correo@empresa.com"
                />
              </label>

              <label className="fc-label">
                Teléfono / WhatsApp*
                <input
                  className="fc-input"
                  value={data.phone}
                  onChange={(e) => setData((p) => ({ ...p, phone: e.target.value }))}
                  required
                  placeholder="+52 ..."
                />
              </label>
            </div>

            <div className="fc-row">
              <label className="fc-label">
                Ciudad / Estado
                <input
                  className="fc-input"
                  value={data.city}
                  onChange={(e) => setData((p) => ({ ...p, city: e.target.value }))}
                  placeholder="Ej. CDMX"
                />
              </label>

            </div>

            <div className="fc-block">
              <div className="fc-label-inline">
                Servicio(s) de interés*
                <span className="fc-hint">Selecciona uno o varios</span>
              </div>

              <div className="fc-chips">
                {serviceChips.map((s) => {
                  const active = data.services.includes(s);
                  return (
                    <button
                      type="button"
                      key={s}
                      className={`fc-chip ${active ? "is-on" : ""}`}
                      onClick={() => toggleService(s)}
                      aria-pressed={active}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>

              {/* Validación simple visual */}
              {data.services.length === 0 && (
                <div className="fc-mini-warn">Selecciona al menos un servicio.</div>
              )}
            </div>

            {/* Detalles “pro” (progresivos) */}
            <button
              type="button"
              className="fc-more"
              onClick={() => setShowDetails((v) => !v)}
              aria-expanded={showDetails}
            >
              {showDetails ? "Ocultar detalles" : "Agregar detalles para cotizar mejor"}
            </button>

            <div className={`fc-details ${showDetails ? "is-open" : ""}`}>
              <div className="fc-row">
                <label className="fc-label">
                  Tipo de proyecto
                  <select
                    className="fc-input"
                    value={data.projectType}
                    onChange={(e) => setData((p) => ({ ...p, projectType: e.target.value }))}
                  >
                    <option value="">Selecciona</option>
                    {PROJECT_TYPES.map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="fc-label">
                  Timeline
                  <select
                    className="fc-input"
                    value={data.timeline}
                    onChange={(e) => setData((p) => ({ ...p, timeline: e.target.value }))}
                  >
                    <option value="">Selecciona</option>
                    {TIMELINES.map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="fc-row">
                <label className="fc-label">
                  Presupuesto aproximado
                  <select
                    className="fc-input"
                    value={data.budget}
                    onChange={(e) => setData((p) => ({ ...p, budget: e.target.value }))}
                  >
                    {BUDGETS.map((x) => (
                      <option key={x} value={x}>
                        {x}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="fc-label" style={{ opacity: 0.85 }}>
                  Adjuntos
                  <div className="fc-attach">
                    Te pedimos fotos/planos en el siguiente contacto (para mantener este formulario simple).
                  </div>
                </div>
              </div>
            </div>

            <label className="fc-label">
              Descripción breve*
              <textarea
                className="fc-input fc-textarea"
                value={data.message}
                onChange={(e) => setData((p) => ({ ...p, message: e.target.value }))}
                required
                placeholder="Cuéntanos el objetivo, el lugar, y lo que quieres lograr..."
              />
            </label>

            <label className="fc-consent">
              <input
                type="checkbox"
                checked={data.consent}
                onChange={(e) => setData((p) => ({ ...p, consent: e.target.checked }))}
              />
              Acepto ser contactado para seguimiento de mi solicitud.
            </label>

            <button
              className="fc-submit"
              type="submit"
              disabled={status === "sending" || data.services.length === 0}
            >
              {status === "sending" ? "Enviando..." : "Enviar solicitud"}
            </button>

            {status === "sent" && (
              <div className="fc-ok">
                ¡Listo! Recibimos tu solicitud. Te contactamos a la brevedad.
              </div>
            )}
            {status === "error" && (
              <div className="fc-err">
                No se pudo enviar en este momento. Intenta más tarde.
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default FormContact;